import React, { ReactElement, useContext } from 'react'

import Fieldset from '../../Common/Components/Fieldset'
import Heading from '../../Common/Components/Heading'
import FormRow from '../../Common/Components/FormRow'
import TextInput from '../../Common/Components/TextInput'
import { Select, SelectRange, SelectRangeItem } from '../../Common/Components/Selects'

import { GenericFilterEntityItem, RptFilterEntityInfo } from '../../Common/Types/Types'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { CallMovementFiltering as CallMovementFilteringLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { ComboBoxAlt } from '../../Common/Components/ComboBox/ComboBoxAlt'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import {
    getCallSegmentEndReasonOptions,
    getCallSegmentStartReasonOptions,
    getSegmentSelectOptions
} from './callMovementFilteringOptions'

// CSS imports. Commented out because of current build process.
// import './callMovementFiltering.scss'

/**
 * Component properties type.
 */
type Props = {
    filterEntityInfo: RptFilterEntityInfo | undefined
    telnosMovedTo: string
    setTelnosMovedTo: (newValue: string) => void
    devicesMovedFrom: string
    setDevicesMovedFrom: (newValue: string) => void
    ACDAgentsMovedFrom: string
    setACDAgentsMovedFrom: (newValue: string) => void
    callSegmentNumber: number
    setCallSegmentNumber: (newValue: number) => void
    callSegmentStartReason: number
    setCallSegmentStartReason: (newValue: number) => void
    callSegmentEndReason: number
    setCallSegmentEndReason: (newValue: number) => void
    callSegmentChainID: string
    setCallSegmentChainID: (newValue: string) => void
    isReportTemplateUser: boolean
    currentTelSysType?: number
}

/**
 * Call movement filtering section in "Other" tab.
 *
 * @param filterEntityInfo The list of entities according to the filter.
 * @param telnosMovedTo Report property.
 * @param setTelnosMovedTo Setter for corresponding report property.
 * @param devicesMovedFrom Report property.
 * @param setDevicesMovedFrom Setter for corresponding report property.
 * @param ACDAgentsMovedFrom Report property.
 * @param setACDAgentsMovedFrom Setter for corresponding report property.
 * @param callSegmentNumber Report property.
 * @param setCallSegmentNumber Setter for corresponding report property.
 * @param callSegmentStartReason Report property.
 * @param setCallSegmentStartReason Setter for corresponding report property.
 * @param callSegmentEndReason Report property.
 * @param setCallSegmentEndReason Setter for corresponding report property.
 * @param callSegmentChainID Report property.
 * @param setCallSegmentChainID Setter for corresponding report property.
 */
const CallMovementFiltering: React.FC<Props> = ({
    filterEntityInfo,
    telnosMovedTo,
    setTelnosMovedTo,
    devicesMovedFrom,
    setDevicesMovedFrom,
    ACDAgentsMovedFrom,
    setACDAgentsMovedFrom,
    callSegmentNumber,
    setCallSegmentNumber,
    callSegmentStartReason,
    setCallSegmentStartReason,
    callSegmentEndReason,
    setCallSegmentEndReason,
    callSegmentChainID,
    setCallSegmentChainID,
    isReportTemplateUser,
    currentTelSysType
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(CallMovementFilteringLanguage.NAME)

    const toEntityOptions = (entityContainerItem: GenericFilterEntityItem | undefined): SelectRangeItem[] =>
        entityContainerItem
            ? entityContainerItem.Data.map((entity) => ({
                  value: entity.ID,
                  renderElement: (
                      <div>
                          {entity.ID} - {entity.Description}
                      </div>
                  )
              }))
            : []

    return (
        <Fieldset className={'call-movement-filtering'}>
            <Heading level={HeadingLevel.ONE}>
                {language.get(CallMovementFilteringLanguage.CallMovementFiltering_SectionHeader)}
            </Heading>
            {!isReportTemplateUser ? (
                <>
                    <FormRow className={'telnos-moved-to'}>
                        <TextInput
                            value={telnosMovedTo}
                            onChange={setTelnosMovedTo}
                            labelLeft={language.get(CallMovementFilteringLanguage.TelnosmovedtoLabel)}
                            placeholder={language.get(
                                CallMovementFilteringLanguage.CallMovementFiltering_AnyPlaceholder
                            )}
                        />
                    </FormRow>
                    <FormRow className={'devices-moved-from'}>
                        <SelectRange
                            items={toEntityOptions(filterEntityInfo?.EntityInfo.Devices)}
                            rangeExpressionValue={devicesMovedFrom}
                            onValueChange={setDevicesMovedFrom}
                            specialAnyItemIndex={0}
                            labelLeft={language.get(CallMovementFilteringLanguage.DevicesmovedfromLabel)}
                        />
                    </FormRow>
                    <FormRow className={'acd-agents-moved-from'}>
                        <SelectRange
                            items={toEntityOptions(filterEntityInfo?.EntityInfo.ACDAgents)}
                            rangeExpressionValue={ACDAgentsMovedFrom}
                            onValueChange={setACDAgentsMovedFrom}
                            specialAnyItemIndex={0}
                            labelLeft={language.getWithTelSysCheck(
                                CallMovementFilteringLanguage.AcdagentsmovedfromLabel,
                                currentTelSysType
                            )}
                        />
                    </FormRow>
                </>
            ) : (
                <></>
            )}
            <FormRow className={'call-segment-number'}>
                <Select
                    label={language.get(CallMovementFilteringLanguage.CallsegmentnumberLabel)}
                    value={callSegmentNumber}
                    handleChange={setCallSegmentNumber}
                    options={getSegmentSelectOptions(language, currentTelSysType)}
                />
            </FormRow>
            <FormRow className={'call-segment-start-reason'}>
                <Select
                    label={language.get(CallMovementFilteringLanguage.CallsegmentstartreasonLabel)}
                    value={callSegmentStartReason}
                    handleChange={setCallSegmentStartReason}
                    options={getCallSegmentStartReasonOptions(language, currentTelSysType)}
                />
            </FormRow>
            <FormRow className={'call-segment-end-reason'}>
                <ComboBoxAlt
                    label={language.get(CallMovementFilteringLanguage.CallsegmentendreasonLabel)}
                    value={callSegmentEndReason}
                    handleChange={(newValue?: number | number[]): void => {
                        if (!IsVarEmpty(newValue) && !Array.isArray(newValue)) {
                            setCallSegmentEndReason(newValue)
                        } else if (Array.isArray(newValue) && newValue.length > 0) {
                            setCallSegmentEndReason(newValue[0])
                        }
                    }}
                    options={getCallSegmentEndReasonOptions(language, currentTelSysType)}
                />
            </FormRow>
            {!isReportTemplateUser ? (
                <FormRow className={'call-segment-chain-id'}>
                    <TextInput
                        value={callSegmentChainID}
                        onChange={setCallSegmentChainID}
                        labelLeft={language.get(CallMovementFilteringLanguage.CallsegmentchainidLabel)}
                        placeholder={language.get(CallMovementFilteringLanguage.CallMovementFiltering_AnyPlaceholder)}
                    />
                </FormRow>
            ) : (
                <></>
            )}
        </Fieldset>
    )
}

export default React.memo(CallMovementFiltering)
